import { useCallback, useState, useMemo } from 'react';
import _orderBy from 'lodash/orderBy';
import { ApolloQueryResult, useApolloClient } from '@apollo/client';

import { getAllListPages, getEntityListLabel, locale } from '@trqk/utils';
import { fields, queryStrings } from '@trqk/resolvers';

import { useStateWithCallback, usePreFilteredList } from '..';

const { GET_TRQK_SONG } = queryStrings;

interface UseDetectionTableItemsResult {
    /**
     * First element of array that results from calling `@xstate/react`'s useMachine method with
     * a machine generated by getPreFilterMachine
     */
    preFilterState: PreFilterState<DetectionsTableItem>;
    /**
     * The array of DetectionsTableItem objects to render in the DetectionsTable
     */
    detectionsTableItems: DetectionsTableItem[];
    /**
     * The array of DetectionTotal objects to render in the DetectionTotalsChart
     */
    detectionTotals: DetectionTotal[];
    /**
     * Gets all paginated query results for listBmatDetections
     */
    fetchAllItems(
        dependencies: [TableBmatDetectionFilterInput],
    ): Promise<DetectionsTableItem[]>;
    /**
     * True before data has been fetched & mapped to detectionsTableItems
     */
    loadingDetectionsTableItems: boolean;
    /**
     * True before data has been fetched & mapped to detectionTotals
     */
    loadingDetectionTotals: boolean;
    /**
     * Second element of array that results from calling `@xstate/react`'s useMachine method with
     * a machine generated by getPreFilterMachine
     */
    sendPreFilterEvent: SendPreFilterEvent<DetectionsTableItem>;
}

interface UseDetectionsTableItemsInput {
    addToast: AddToast;
    filterInput: TableBmatDetectionFilterInput;
    getToken: GetToken;
    preFilterConfigs: Map<string, PreFilterConfig>;
    trqkDirectoryUrl: string;
}

/**
 * A hook that provides the lists of data for rendering the DetectionsTable and the DetectionTotals
 * chart, as well as some related state and methods.
 * @param defaultDateRange the initial date range for the filterInput - see {@link MaybeEmptyDateRange}
 * @returns a {@link UseDetectionTableItemsResult}
 */
export const useDetectionsTableItems = ({
    addToast,
    filterInput,
    getToken,
    preFilterConfigs,
    trqkDirectoryUrl,
}: UseDetectionsTableItemsInput): UseDetectionTableItemsResult => {
    const [loadingDetectionsTableItems, setLoadingDetectionsTableItems] =
        useState(true);

    const [loadingDetectionTotals, setLoadingDetectionTotals] = useState(true);

    // Using a memoized callback prevents a stack overflow in `useStateWithCallback`.
    const onSetDetectionTotals = useCallback((): void => {
        setLoadingDetectionTotals(false);
    }, []);

    // detectionTotals is used to render the chart of the top 5 detection tracks above the table
    // of detection data. For each bmatDetection, there should be an item within detectionTotals
    // where the title is either the `bmatDetection.filename` (if there is no corresponding
    // trqkSong) or the `trqkSong.title` (if there is a corresponding trqkSong).
    const [detectionTotals, setDetectionTotals] = useStateWithCallback<
        DetectionTotal[]
    >([], onSetDetectionTotals);

    const client = useApolloClient();

    const fetchAllItems = useCallback(
        async (
            dependencies: [TableBmatDetectionFilterInput],
        ): Promise<DetectionsTableItem[]> => {
            const filter = dependencies[0];

            setLoadingDetectionsTableItems(true);

            let bmatDetections: BmatDetection[] = [];

            try {
                bmatDetections = await getAllListPages<
                    BmatDetection,
                    TableBmatDetectionFilterInput
                >({
                    filter,
                    getToken,
                    query: `
                            query listBmatDetections(
                                $filter: TableBmatDetectionFilterInput
                                $nextToken: String
                                $limit: Int
                            ) {
                                listBmatDetections(
                                    filter: $filter
                                    nextToken: $nextToken
                                    limit: $limit
                                ) {
                                    items {
                                        ${fields.LIST_BMAT_DETECTIONS_ITEM_FIELDS}
                                    }
                                    nextToken
                                }
                            }
                        `,
                    queryName: 'listBmatDetections',
                    trqkDirectoryUrl,
                });
            } catch {
                setLoadingDetectionTotals(false);
                setLoadingDetectionsTableItems(false);

                throw new Error('Failed to query listBmatDetections');
            }

            const allItems: DetectionsTableItem[] = [];

            bmatDetections.forEach((bmatDetection: BmatDetection): void => {
                allItems.push({
                    ...bmatDetection,
                    title: null,
                    workIds: null,
                    publisher: null,
                    writer: null,
                });
            });

            setLoadingDetectionsTableItems(false);

            return allItems;
        },
        [addToast, getToken],
    );

    const onFetchAllItemsError = () => {
        addToast({
            kind: 'warning-banner',
            content: locale.enUS.DETECTIONS.ERRORS.FETCH_DETECTIONS_ERROR,
        });
    };

    const onFetchAllItemsSuccess = async (
        next: DetectionsTableItem[],
        send: SendPreFilterEvent<DetectionsTableItem>,
    ) => {
        const trqkSongIds: string[] = [];

        // Set up an array of Promises, one for each trqkAudio that has a `song_id`. Each
        // Promise will do a `getTrqkSong` query and then add trqkSong data to the appropriate
        // item in DetectionsTableItems and DetectionTotals.
        // Note that we can't use Apollo Client's `useLazyQuery` hook for this because it is
        // blocking; i.e. it doesn't start the next query until the current one finishes.
        const getTrqkSongPromises = trqkSongIds.map(
            (trqkSongId: string): Promise<TrqkSong> =>
                new Promise<TrqkSong>((resolve, reject) => {
                    client
                        .query<{ getTrqkSong: TrqkSong }>({
                            query: GET_TRQK_SONG,
                            variables: {
                                id: trqkSongId,
                            },
                        })
                        .then(
                            (
                                result: ApolloQueryResult<{
                                    getTrqkSong: TrqkSong;
                                }>,
                            ): void => {
                                const trqkSong =
                                    result?.data?.getTrqkSong ?? null;

                                if (trqkSong === null) {
                                    throw new Error(
                                        `Unable to fetch trqkSong for song_id: ${trqkSongId}`,
                                    );
                                }

                                resolve(trqkSong);
                            },
                        )
                        .catch((error: Error): void => {
                            reject(error);
                        });
                }),
        );

        // Call all `getTrqkSong` queries in parallel
        const getTrqkSongResults = await Promise.allSettled(
            getTrqkSongPromises,
        );

        const { trqkSongs, errors } = getTrqkSongResults.reduce(
            (
                acc: { trqkSongs: TrqkSong[]; errors: Error[] },
                curr: PromiseSettledResult<TrqkSong>,
            ): { trqkSongs: TrqkSong[]; errors: Error[] } => {
                if (curr.status === 'rejected') {
                    acc.errors.push(curr.reason as Error);
                } else {
                    acc.trqkSongs.push(curr.value);
                }

                return acc;
            },
            { trqkSongs: [], errors: [] },
        );

        if (errors.length > 0) {
            addToast({
                kind: 'warning-banner',
                content: locale.enUS.DETECTIONS.ERRORS.FETCH_TRQK_SONG_ERROR,
            });
        }

        const itemsWithSongData = next.map(
            (item: DetectionsTableItem): DetectionsTableItem => {
                const trqkSong = trqkSongs.find(
                    ({ id }: TrqkSong): boolean => item.song_id === id,
                );

                if (trqkSong) {
                    return {
                        ...item,
                        title: trqkSong?.title ?? null,
                        workIds: trqkSong.work_ids ?? null,
                        publisher: getEntityListLabel(
                            trqkSong,
                            'publisher' as ProRoleType,
                        ),
                        writer: getEntityListLabel(
                            trqkSong,
                            'writer' as ProRoleType,
                        ),
                    };
                }

                return item;
            },
        );

        const detectionTotalsByAudioId = itemsWithSongData.reduce(
            (
                acc: Record<string, DetectionTotal>,
                curr: DetectionsTableItem,
            ): Record<string, DetectionTotal> => {
                const audio_id = curr?.audio_id ?? null;
                const filename = curr?.filename ?? null;
                const title = curr?.title ?? null;

                if (audio_id && filename) {
                    const currentDetectionsCount =
                        acc?.[audio_id]?.detections ?? 0;

                    return {
                        ...acc,
                        [audio_id]: {
                            detections: currentDetectionsCount + 1,
                            audio_id,
                            filename,
                            title,
                        },
                    };
                }

                return acc;
            },
            {},
        );

        setDetectionTotals(Object.values(detectionTotalsByAudioId));

        send({
            type: 'SET_ALL_ITEMS',
            value: {
                allItems: itemsWithSongData,
                isInitialQuery: false,
            },
        });
    };

    const { current, send } = usePreFilteredList<DetectionsTableItem>({
        fetchAllItems,
        fetchAllItemsDeps: [filterInput],
        onFetchAllItemsError,
        onFetchAllItemsSuccess,
        preFilterConfigs,
    });

    const sortedAndSlicedDetectionTotals = useMemo((): DetectionTotal[] => {
        const sorted = _orderBy(
            detectionTotals,
            ['detections', 'filename'],
            ['desc', 'asc'],
        );

        return sorted.slice(0, 5).reverse();
    }, [detectionTotals]);

    return {
        preFilterState: current,
        detectionsTableItems: current.context.filteredItems,
        detectionTotals: sortedAndSlicedDetectionTotals,
        fetchAllItems,
        loadingDetectionsTableItems,
        loadingDetectionTotals,
        sendPreFilterEvent: send,
    };
};
